import * as React from "react";
import moment from "moment";
import { RdPopover } from "../../redesign/table/RdPopover";
import { RD_MONTH_GRID_BREITE, RdMonthGrid } from "../../calendar/RdDatePicker";
import { kwText } from "../../calendar/kalenderwoche";
import { toDateInputValue } from "../../../utils/dateUtils";
import { toIsoDate, twvDateView } from "../detail/twvFrist";
import "../../../utils/momentDe"; // zentrale de-Locale-Quelle (Wochentagskürzel)
import "../v3/vorgangV3.css";
import "./vorgangV4.css";

/**
 * TWV und Frist in V4 als **Wertchip**, nicht als Formularfeld.
 *
 *     TWV [ Do., 18.09.2026 ]   FRIST [ — ]
 *
 * Der Grund ist der größte Einzelposten des Soll-Ist-Vergleichs vom
 * 13.09.2026: `<input type="date">` bringt die Eingabehilfe des Browsers mit —
 * Rahmen, Innenpolster, Trennzeichen, Kalendersymbol — und ist damit rund
 * doppelt so hoch und breit wie die Anzeige, die es trägt. Die Vorlage zeigt
 * an dieser Stelle eine **leise Anzeige**; das Formular öffnet sich erst auf
 * Klick (`RdMonthGrid` im Popover, dasselbe Kalenderblatt wie in
 * `TodoDatumStapel`).
 *
 * BEWUSST EINE NEUE KOMPONENTE NEBEN `FristTwvFeld`, kein Umbau davon:
 * `DatumFeld` trägt der `TodoAnlegenKasten` — dort bleibt das Eingabefeld,
 * wie es war. Hier ändert sich genau eine Stelle.
 *
 * Die Anzeige nennt IMMER das Jahr („Do., 18.09.2026", Mockup `.frist .wert`).
 * `twvDateView().date` lässt das laufende Jahr weg — richtig für eine
 * Listenzeile, in der zwanzig Daten untereinander stehen; hier steht genau
 * eines, und eine Wiedervorlage ohne Jahr ist die häufigste Verlesung.
 * Das relative Wort („überfällig", „heute") bleibt daneben stehen wie bisher:
 * es ist die einzige Stelle, an der eine überfällige Frist auffällt.
 */

/** Der Chiptext eines Datums — „Do., 18.09.2026"; leer der Gedankenstrich. */
export const DATUM_LEER = "—";

export function datumChipText(v?: string | null): string {
    const iso = toIsoDate(v);
    if (!iso) return DATUM_LEER;
    const m = moment(iso, "YYYY-MM-DD", true);
    return m.isValid() ? m.format("dd., DD.MM.YYYY") : DATUM_LEER;
}

const parseIso = (v: string): Date => (v ? new Date(`${v}T00:00:00`) : new Date());

/**
 * Tooltip und Vorlesetext des Chips: „TWV: Do., 15.10.2026 · KW 42" — die
 * Kalenderwoche (T-194, 05.10.2026), weil Abwesenheiten oft so genannt werden
 * („erst in der 43. KW wieder im Hause"). Ohne Datum: „TWV setzen".
 */
export function datumChipTitel(label: string, v?: string | null): string {
    const iso = toIsoDate(v);
    if (!iso) return `${label} setzen`;
    const text = datumChipText(iso);
    const m = moment(iso, "YYYY-MM-DD", true);
    return m.isValid() ? `${label}: ${text} · ${kwText(m.toDate())}` : `${label}: ${text}`;
}

/**
 * Das relative Wort neben dem Chip („heute", „überfällig" …) — oder `null`.
 *
 * T-218 (06.10.2026): An einer ERLEDIGTEN Karte steht kein rotes
 * „überfällig" mehr. Erledigen leert TWV und Frist zwar serverseitig; der
 * Altbestand und von Hand nachgetragene Termine tragen aber weiter ein
 * vergangenes Datum, und an einem erledigten Eintrag ist nichts überfällig.
 */
export function relativFuerChip(
    iso: string | null | undefined,
    erledigt = false,
    now: Date = new Date(),
): { text: string; tone: string } | null {
    const view = twvDateView(toIsoDate(iso), now);
    if (!view?.relative) return null;
    if (erledigt && view.tone === "overdue") return null;
    return { text: view.relative, tone: view.tone };
}

type Props = {
    /** Name des Feldes für Vorlesewerkzeuge und Tooltip („Frist des To-dos"). */
    label: string;
    value?: string | null;
    canEdit: boolean;
    onSave: (next: string | null) => void;
    /** Erledigte Karte: kein rotes „überfällig" (T-218). Der Chip bleibt bedienbar. */
    erledigt?: boolean;
};

export const DatumChipV4: React.FC<Props> = ({ label, value, canEdit, onSave, erledigt = false }) => {
    const iso = toIsoDate(value);
    const rel = relativFuerChip(iso, erledigt);
    const text = datumChipText(iso);
    const klasse = `vg4-datum${iso ? "" : " leer"}`;
    const beschriftung = datumChipTitel(label, iso);

    const relativ = rel ? <span className={`vg3-rel ${rel.tone}`}>{rel.text}</span> : null;

    // Ohne Bearbeitungsrecht ist der Chip reine Anzeige — ein Knopf, der nichts
    // öffnen darf, wäre ein leeres Versprechen.
    if (!canEdit) {
        return (
            <>
                <span className={klasse} title={beschriftung}>
                    {text}
                </span>
                {relativ}
            </>
        );
    }

    return (
        <>
            <RdPopover
                align="start"
                width={RD_MONTH_GRID_BREITE}
                dialogLabel={label}
                trigger={({ open, toggle, ref }) => (
                    <button
                        ref={ref}
                        type="button"
                        className={klasse}
                        title={beschriftung}
                        aria-label={beschriftung}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        onClick={toggle}
                    >
                        {text}
                    </button>
                )}
            >
                {(close) => (
                    // Portal an `body` — außerhalb des `.kvp-rd`-Wrappers der
                    // Seite; ohne eigene Hülle griffen weder Regeln noch Tokens.
                    <div className="kvp-rd vg4-datum-wahl">
                        <RdMonthGrid
                            value={parseIso(iso)}
                            onPick={(d) => {
                                onSave(toDateInputValue(d));
                                close();
                            }}
                        />
                        {iso && (
                            <button
                                type="button"
                                className="vg3-tbtn vg4-datum-weg"
                                onClick={() => {
                                    onSave(null);
                                    close();
                                }}
                            >
                                Datum entfernen
                            </button>
                        )}
                    </div>
                )}
            </RdPopover>
            {relativ}
        </>
    );
};
